<template>
    <div>
        <p class="heading has-text-grey-light mb-4">
            <span class="icon-text">
                <span class="icon"><i class="fas fa-tv"></i></span>
                <span>{{ translate("discipline_packages.scoreboard_section") }}</span>
            </span>
        </p>

        <div v-if="isOverride" class="field mb-4">
            <Checkbox2 :model-value="isOverriding" @change="toggleOverride">
                {{ translate("discipline_packages.scoreboard_override_enable") }}
            </Checkbox2>
        </div>

        <div v-if="!isOverride || isOverriding" class="columns">
            <div class="column is-6">
                <div class="field">
                    <label class="label">{{ translate("discipline_packages.scoreboard_type") }}</label>
                    <div class="control">
                        <div class="select is-fullwidth">
                            <select :value="currentType" @change="onTypeChange">
                                <option v-for="type in sortedScoreboardTypes" :key="type" :value="type">
                                    {{ translate("scoreboard.type." + type) }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="(!isOverride || isOverriding) && hasAnySettings" class="columns is-multiline">
            <div class="column is-12">
                <KataScoreboardSettings v-if="currentType === 'kata'" />
                <MmaScoreboardSettings v-if="currentType === 'mmaBasic'" />
                <WrestlingScoreboardSettings v-if="currentType === 'wrestling' || currentType === 'grappling'" />
            </div>
        </div>

        <p v-if="isOverride && !isOverriding" class="has-text-grey-light is-size-7">
            {{ translate("discipline_packages.scoreboard_inherits_from_default") }}
        </p>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../Common/translations";
    import { useDisciplinePackagesStore } from "./disciplinePackagesStore";
    import { ScoreboardType } from "../Scoreboard/Common/types";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import KataScoreboardSettings from "./ScoreboardSettings/KataScoreboardSettings.vue";
    import MmaScoreboardSettings from "./ScoreboardSettings/MmaScoreboardSettings.vue";
    import WrestlingScoreboardSettings from "./ScoreboardSettings/WrestlingScoreboardSettings.vue";
    import { resetScoreboardSettingsForType } from "./ScoreboardSettings/scoreboardSettingsAdapters";

    const props = withDefaults(defineProps<{ isOverride?: boolean }>(), { isOverride: false });

    const store = useDisciplinePackagesStore();

    const scoreboardTypes = computed(() => store.currentScoreboardTypes);
    const sortedScoreboardTypes = computed(() =>
        [...scoreboardTypes.value].sort((a, b) => translate("scoreboard.type." + a).localeCompare(translate("scoreboard.type." + b))),
    );
    const hasAnySettings = computed(() => Object.keys(store.selectedScoreboardSettings).length > 0);

    const isOverriding = computed(() => {
        if (!props.isOverride || store.activeTabIndex === null) {
            return false;
        }
        return store.disciplineOverrideConfigs[store.activeTabIndex]?.scoreboardType !== null;
    });

    const currentType = computed(() => {
        if (props.isOverride && store.activeTabIndex !== null) {
            return store.disciplineOverrideConfigs[store.activeTabIndex]?.scoreboardType ?? store.disciplineDefaultConfig.scoreboardType;
        }
        return store.disciplineDefaultConfig.scoreboardType;
    });

    function toggleOverride(checked: boolean): void {
        if (store.activeTabIndex === null) {
            return;
        }
        store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardType = checked
            ? store.disciplineDefaultConfig.scoreboardType
            : null;
        if (!checked) {
            store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = null;
        }
    }

    function onTypeChange(event: Event): void {
        const value = (event.target as HTMLSelectElement).value as ScoreboardType;
        if (props.isOverride && store.activeTabIndex !== null) {
            store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardType = value;
            store.disciplineOverrideConfigs[store.activeTabIndex].scoreboardSettings = resetScoreboardSettingsForType(value);
        } else {
            store.disciplineDefaultConfig.scoreboardType = value;
            store.disciplineDefaultConfig.scoreboardSettings = resetScoreboardSettingsForType(value);
        }
    }
</script>

<style scoped></style>
